/**
 * The Settings brand switcher: a dropdown of the workspace's brands with their
 * marks, the same control as the workspace switcher. It only changes which
 * brand Settings is about (and the app's selected brand); it never opens the
 * rail's brand picker.
 */
import { useEffect, useState } from "react";
import { DialogTrigger, Pressable } from "react-aria-components";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/utils/helpers";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { KitPopover } from "../../kit/Popover";
import { kitDropdownMenuItemActiveClass, kitDropdownMenuItemClass } from "../../kit/dropdownStyles";
import { BrandMark } from "../../kit/BrandPicker";
import { ScopeTile } from "./SettingsParts";
import { onOpenSettingsBrandPicker, useSelectedBrand } from "./scope";

export const SWITCHER_PILL =
  "co-glass-control flex h-9 w-full items-center gap-2.5 rounded-pill pl-2.5 pr-3 text-left transition-transform active:scale-[0.98]";

/** `listen`: the page heads' "Change" opens this one (the nav's). */
export function BrandSwitcher({ listen = false }: { listen?: boolean }) {
  const { brand, brands } = useSelectedBrand();
  const setSelectedBrandId = useWorkspaceStore((s) => s.setSelectedBrandId);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => (listen ? onOpenSettingsBrandPicker(() => setIsOpen(true)) : undefined), [listen]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Pressable>
        <button type="button" className={SWITCHER_PILL} aria-label={brand ? `Brand: ${brand.name}. Switch brand` : "Choose a brand"}>
          {brand ? <BrandMark brand={brand} size={22} /> : <ScopeTile name="?" size={22} />}
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-co-text">
            {brand?.name ?? "Choose a brand"}
          </span>
          <ChevronsUpDown aria-hidden className="size-3.5 shrink-0 text-co-muted" />
        </button>
      </Pressable>
      <KitPopover placement="bottom start" offset={8} className="max-h-[360px] min-w-[var(--trigger-width)] overflow-y-auto p-1.5">
        {brands.length === 0 ? (
          <p className="px-2.5 py-2 text-[13px] text-co-muted">This workspace has no brands yet.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {brands.map((b) => {
              const active = b.id === brand?.id;
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBrandId(b.id);
                      setIsOpen(false);
                    }}
                    className={cn(kitDropdownMenuItemClass, active && kitDropdownMenuItemActiveClass)}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <BrandMark brand={b} size={22} />
                      <span className="truncate">{b.name}</span>
                    </span>
                    {active && <Check className="size-3.5 shrink-0 text-co-accent" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </KitPopover>
    </DialogTrigger>
  );
}
